import React, { useMemo, ReactNode } from 'react';
import { Ticket, TicketStatus } from '../types';
import { TicketCard } from './TicketCard';
import { DraggableSlider } from './DraggableSlider';
import {
	ClockCircleOutlined,
	SyncOutlined,
	CheckCircleOutlined,
	FormOutlined,
} from '@ant-design/icons';
import './FormResponsesComponents.scss';

interface TicketViewProps {
	tickets: Ticket[];
	onStatusChange: (ticketId: string, status: TicketStatus) => void;
	onNoteSave: (ticketId: string, note: string) => void;
	onViewDetails: (ticket: Ticket) => void;
	onViewCallDetails: (ticket: Ticket) => void;
}

const TicketSection: React.FC<{
	title: string;
	tickets: Ticket[];
	icon: ReactNode;
	handlers: Omit<TicketViewProps, 'tickets'>;
}> = ({ title, tickets, icon, handlers }) => (
	<div>
		<h3 className="ticket-section-header">
			{icon}
			{title} ({tickets.length})
		</h3>
		{tickets.length > 0 ? (
			<DraggableSlider>
				{tickets.map((ticket) => (
					<div key={ticket.id} style={{ minWidth: '340px', maxWidth: '340px' }}>
						<TicketCard ticket={ticket} {...handlers} />
					</div>
				))}
			</DraggableSlider>
		) : (
			<div className="empty-section-placeholder">
				<p>No tickets in this section.</p>
			</div>
		)}
	</div>
);

export const TicketView: React.FC<TicketViewProps> = ({ tickets, ...handlers }) => {
	const openTickets = useMemo(() => tickets.filter((t) => t.status === 'OPEN'), [tickets]);
	const inProgressTickets = useMemo(
		() => tickets.filter((t) => t.status === 'IN_PROGRESS'),
		[tickets],
	);
	const completedTickets = useMemo(
		() => tickets.filter((t) => t.status === 'COMPLETED'),
		[tickets],
	);

	if (tickets.length === 0) {
		return (
			<div className="empty-page-placeholder">
				<FormOutlined />
				<h3>No Tickets Found</h3>
				<p>Responses from your intake forms will appear here automatically.</p>
			</div>
		);
	}

	return (
		<div className="ticket-view-container">
			<TicketSection
				title="Open"
				tickets={openTickets}
				icon={<ClockCircleOutlined style={{ color: '#3b82f6' }} />}
				handlers={handlers}
			/>
			<TicketSection
				title="In Progress"
				tickets={inProgressTickets}
				icon={<SyncOutlined style={{ color: '#ca8a04' }} />}
				handlers={handlers}
			/>
			<TicketSection
				title="Completed"
				tickets={completedTickets}
				icon={<CheckCircleOutlined style={{ color: '#16a34a' }} />}
				handlers={handlers}
			/>
		</div>
	);
};
